import React, { useRef, useEffect, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useNavigate } from "react-router-dom";

const AboutUsHeroSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { amount: 0.3 }); // Trigger when 30% visible
  const navigation = useNavigate();

  const [scrollDirection, setScrollDirection] = useState("down");

  // Detect scroll direction
  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const currentY = window.scrollY;
      if (currentY > lastY) {
        setScrollDirection("down");
      } else if (currentY < lastY) {
        setScrollDirection("up");
      }
      lastY = currentY;
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const topVariants = {
    hidden: { opacity: 0, y: -80 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 1.8, ease: [0.25, 0.1, 0.25, 1] },
    },
  };

  const bottomVariants = {
    hidden: { opacity: 0, y: 80 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 1.8, ease: [0.25, 0.1, 0.25, 1] },
    },
  };

  const statsContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.6 } },
  };

  const statItem = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 1.6, ease: [0.25, 0.1, 0.25, 1] },
    },
  };

  const [autoAnimate, setAutoAnimate] = useState(false);
  const [iconSrc, setIconSrc] = useState(
    "/images/AboutUs/icons/arrow-right.svg"
  );

  useEffect(() => {
    const isMobile = () => window.matchMedia("(max-width: 767.98px)").matches;
    let intervalId;
    if (isMobile()) {
      setAutoAnimate(true);
      let toggle = false;
      intervalId = setInterval(() => {
        toggle = !toggle;
        setIconSrc(
          toggle
            ? "/public/images/AboutUs/icons/arrow-right-hover.svg"
            : "/images/AboutUs/icons/arrow-right.svg"
        );
      }, 2200);
    } else {
      setAutoAnimate(false);
      setIconSrc("/images/AboutUs/icons/arrow-right.svg");
    }
    const handleResize = () => {
      if (window.matchMedia("(max-width: 767.98px)").matches) {
        setAutoAnimate(true);
      } else {
        setAutoAnimate(false);
        setIconSrc("/images/AboutUs/icons/arrow-right.svg");
      }
    };
    window.addEventListener("resize", handleResize);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Decide animation based on scroll direction
  const getAnimateState = () => {
    if (isInView) return "visible";
    if (!isInView && scrollDirection === "up") return "hidden"; // reverse naturally
    return "hidden";
  };

  return (
    <div
      ref={sectionRef}
      className="aboutus-herosection d-flex flex-column justify-content-center align-items-center"
    >
      {/* Top Section */}
      <motion.button
        className="aboutus-herosection__about-btn mb-3"
        variants={topVariants}
        initial="hidden"
        animate={getAnimateState()}
      >
        About SprigStack
      </motion.button>

      <motion.h1
        className="aboutus-herosection__heading text-center mb-4 mx-auto"
        variants={topVariants}
        initial="hidden"
        animate={getAnimateState()}
      >
        Since 2016,
        <span className="aboutus-herosection__highlight"> SprigStack</span> has
        been the force behind remarkable
        <span className="aboutus-herosection__highlight">
          {" "}
          software solutions
        </span>{" "}
        that truly move the needle.
      </motion.h1>

      {/* Let's Talk Button */}
      <motion.button
        className={`aboutus-herosection__lets-talk-btn mt-3${
          autoAnimate ? " auto-animate" : ""
        }`}
        variants={bottomVariants}
        initial="hidden"
        animate={getAnimateState()}
        tabIndex={autoAnimate ? -1 : 0}
        onClick={() => navigation("/contact-us")}
        style={autoAnimate ? { pointerEvents: "none" } : {}}
      >
        <span className="text-slide-wrap">
          <span className="text-slide-original">Let’s talk</span>
          <span className="text-slide-hover">Let’s talk</span>
        </span>
        <img
          src={iconSrc}
          alt="arrow"
          className="aboutus-herosection__arrow-img"
        />
      </motion.button>

      {/* Stats */}
      <motion.div
        className="aboutus-herosection__stats-row"
        variants={statsContainer}
        initial="hidden"
        animate={getAnimateState()}
      >
        {[
          { label: "Projects Completed", value: "100+" },
          { label: "Happy Clients", value: "50+" },
          { label: "Experts In Team", value: "30+" },
          { label: "Successful Years", value: "9+" },
        ].map((item, idx) => (
          <motion.div
            className="aboutus-herosection__stat"
            key={idx}
            variants={statItem}
          >
            <div className="aboutus-herosection__stat-label-wrapper">
              <img
                src="/images/AboutUs/icons/arrow-left-linear.svg"
                alt="arrow icon"
                className="aboutus-herosection__stat-icon"
              />
              <span className="aboutus-herosection__stat-label">
                {item.label}
              </span>
            </div>
            <div className="aboutus-herosection__stat-value">{item.value}</div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

export default AboutUsHeroSection;
